import type { HTMLAttributes, ReactNode } from 'react';
import { cn } from '../../lib/cn';

export type ChipTone = 'outline' | 'neutral' | 'navy' | 'info' | 'success' | 'warning' | 'danger' | 'coral';
export type ChipSize = 'sm' | 'md' | 'lg';

interface ToneStyle {
  readonly bg: string;
  readonly fg: string;
  readonly dot: string;
  readonly ring: string;
}

/**
 * The handoff screens' chip tones, plus `coral` from the DS Chip. Each chip draws
 * a 1px inset ring; only `outline` makes it visible.
 */
export const TONE: Record<ChipTone, ToneStyle> = {
  neutral: {
    bg: 'var(--neutral-100)',
    fg: 'var(--neutral-600)',
    dot: 'var(--neutral-400)',
    ring: 'transparent',
  },
  navy: { bg: 'var(--pi-navy-tint)', fg: 'var(--pi-navy)', dot: 'var(--pi-navy)', ring: 'transparent' },
  info: { bg: 'var(--info-bg)', fg: 'var(--info-fg)', dot: 'var(--info)', ring: 'transparent' },
  warning: { bg: 'var(--warning-bg)', fg: 'var(--warning-fg)', dot: 'var(--warning)', ring: 'transparent' },
  success: { bg: 'var(--success-bg)', fg: 'var(--success-fg)', dot: 'var(--success)', ring: 'transparent' },
  danger: { bg: 'var(--danger-bg)', fg: 'var(--danger-fg)', dot: 'var(--danger)', ring: 'transparent' },
  outline: { bg: 'transparent', fg: 'var(--fg-2)', dot: 'var(--neutral-300)', ring: 'var(--border-2)' },
  coral: { bg: 'var(--pi-coral)', fg: 'var(--pi-navy)', dot: 'var(--pi-navy)', ring: 'transparent' },
};

/*
 * Sizes used by the screens: sm = in-cell flags, stages, PDF queue,
 * dot-less tag pills (20px, 0 8px, 11px); md = table and drawer status (22px, 0 9px, 12px);
 * lg = profile and page headers (24px, 0 10px, 12px). All 500 weight, pill radius.
 */
const SIZES: Record<ChipSize, string> = {
  sm: 'h-5 px-2 text-[11px]',
  md: 'h-[22px] px-[9px] text-[12px]',
  lg: 'h-6 px-2.5 text-[12px]',
};

export interface StatusChipProps extends HTMLAttributes<HTMLSpanElement> {
  readonly tone?: ChipTone;
  readonly size?: ChipSize;
  /** The 6px status dot; tag pills such as "Archived" have none. */
  readonly dot?: boolean;
  readonly children: ReactNode;
}

export function StatusChip({
  tone = 'neutral',
  size = 'md',
  dot = true,
  className,
  style,
  children,
  ...rest
}: StatusChipProps) {
  const t = TONE[tone];
  return (
    <span
      data-tone={tone}
      className={cn(
        'inline-flex items-center gap-1.5 whitespace-nowrap rounded-pill font-medium leading-none',
        SIZES[size],
        className,
      )}
      style={{ background: t.bg, color: t.fg, boxShadow: `inset 0 0 0 1px ${t.ring}`, ...style }}
      {...rest}
    >
      {dot && (
        <span aria-hidden="true" className="size-1.5 flex-none rounded-full" style={{ background: t.dot }} />
      )}
      {children}
    </span>
  );
}
